import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import Datagrid from "@bit/the-glue.frontendcomponents.datagrid";
import SearchBar from "@bit/the-glue.frontendcomponents.search-bar";
import { HEADINGS, searchMap } from "./constants";
import { removeBadge, setBadgesList, unArchiveBadge } from "./_redux/actions";
import { modifyBadgesList, renderBadgesRow } from "./helpers";
import { ListButton } from "../../../../ui/components/ListButton";
import { ListHeader } from "../../../../ui/structures/ListHeader";
import { useFetch } from "../../../../hooks/fetch.hook";
import { deleteBadge, getBadgeById, getBadges, modifyBadge } from "./_api";
import { Modal } from "../../../../ui/components/Modal";
import { ConfirmDelete } from "../../../../ui/components/ConfirmDelete";
import { ArchiveCheckbox } from "../../../../ui/components/ArchiveCheckbox";
import { getSelectedId, handleSearch } from "../../../../ui/helpers";
import { UnarchiveButton } from "../../../../ui/components/UnarchiveButton";
import { sortHeaderStyle } from "../../../constants";
import { TableSortLabel } from "@material-ui/core";
import { Loader } from "../../../../ui/components/Loader";
import { info } from "../../../../helpers/toasts";

export const List = () => {
  const dispatch = useDispatch();
  const { request } = useFetch();

  const [loading, setLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState("");
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archived, setArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [unArchiving, setUnarchiving] = useState(false);
  const [field, setField] = useState("");
  const [direction, setDirection] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const handleModalClose = () => setConfirmDeleteOpen(false);

  const handleOpenModal = id => {
    setConfirmDeleteOpen(true);
    setSelectedItem(id);
  };

  const data = useSelector(({ badges: { badgesList } }) => badgesList);

  const fetchBadges = () => {
    setLoading(true);
    request(getBadges, search, archived, field, field && direction)
      .then(data => {
        dispatch(setBadgesList(modifyBadgesList(data)));
        setLoading(false);
      })
      .finally(() => setArchivedLoading(false));
  };

  useEffect(() => {
    fetchBadges();
    // eslint-disable-next-line
  }, [search, archived, field, direction]);

  const showArchived = () => {
    setArchivedLoading(true);
    setArchived(!archived);
  };

  const unArchiveDisabled = useMemo(
    () => Object.values(selected).filter(Boolean).length !== 1,
    [selected]
  );

  const handleUnarchive = () => {
    const badgeID = getSelectedId(selected);
    const status = (data.find(({ id }) => id === badgeID) || {}).status;
    if (status !== "archived") return;
    setUnarchiving(true);
    setActionLoading(true);
    request(modifyBadge, { status: "active" }, badgeID)
      .then(data => {
        if (!data) return;
        dispatch(unArchiveBadge(data));
        setSelected({});
        info("Badge has been unarchived!");
      })
      .finally(() => {
        setUnarchiving(false);
        setActionLoading(false);
      });
  };

  const handleDelete = (id, setSubmitting) => {
    setActionLoading(true);
    setConfirmDeleteOpen(false);
    request(deleteBadge, id)
      .then(data => {
        if (!data) return;
        if (archived) {
          request(getBadgeById, id).then(
            data => data && dispatch(unArchiveBadge(data))
          );
        } else dispatch(removeBadge(id));
        info("Badge has been archived!");
        setSelected({});
      })
      .finally(() => {
        setActionLoading(false);
      });
  };

  const KEY_MAP = {
    "Badge Name": data.map(item => item.name),
    "Contact First Name": data.map(item => item.contact?.first_name),
    "Contact Last Name": data.map(item => item.contact?.last_name)
  };

  const resetSearch = () => {
    setSearch("");
  };

  const renderButtons = () => (
    <>
      <div className="mr-10">
        <Link
          to={{
            pathname: `/add-badge`
          }}
          data-testid="new"
        >
          <ListButton label="New" />
        </Link>
      </div>
      {archived && (
        <UnarchiveButton
          handleClick={handleUnarchive}
          disabled={unArchiveDisabled}
          loading={unArchiving}
        />
      )}
    </>
  );

  function renderHeaderWithSorting(headings) {
    return headings.map(([key, header]) => (
      <th
        key={key}
        style={sortHeaderStyle}
        className="px-5 text-nowrap"
        onClick={() => {
          setDirection(
            key !== field ? "desc" : direction === "desc" ? "asc" : "desc"
          );
          setField(key);
        }}
      >
        <span style={{ cursor: "pointer" }}>{header}</span>
        {key === field && <TableSortLabel active direction={direction} />}
      </th>
    ));
  }

  return (
    <div className="row justify-content-center mt-10">
      <div className="col-12">
        {actionLoading && <Loader isOpen={actionLoading} maxWidth="xs" />}
        {confirmDeleteOpen && (
          <Modal
            isOpen={confirmDeleteOpen}
            submitable
            onClose={handleModalClose}
            maxWidth="sm"
            modalContent={
              <ConfirmDelete
                handleClose={handleModalClose}
                handleSubmit={handleDelete}
                id={selectedItem}
                name={(data.find(({ id }) => id === selectedItem) || {}).name}
              />
            }
          />
        )}
        <div>
          <SearchBar
            onSearch={data => handleSearch(data, searchMap, setSearch)}
            clearSearch={resetSearch}
            keyMap={KEY_MAP}
            placeholder="Filter Badges..."
            elevation={2}
            chipBackgroundColor="#E8F5E1"
            chipColor="#255915"
          />
        </div>
        <div className="bg-white rounded py-7 px-10">
          <ListHeader title="Badges" renderButtons={renderButtons} />
          <ArchiveCheckbox
            archivedLoading={archivedLoading}
            archived={archived}
            showArchived={showArchived}
          />
          <Datagrid
            data={data}
            headings={HEADINGS}
            renderRow={renderBadgesRow}
            renderHeaderWithSorting={renderHeaderWithSorting}
            selectable
            selected={selected}
            setSelected={setSelected}
            editable
            isNewTabLink
            deletable
            handleDelete={handleOpenModal}
            loading={loading}
            link="badges"
            paginationOptions={[50, 100, 200]}
            paginationDefaultValue={50}
          />
        </div>
      </div>
    </div>
  );
};
